import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

const SecurityDashboard = () => {
  const [securityStats, setSecurityStats] = useState(null);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    eventType: '',
    severity: '',
    limit: 50
  });

  useEffect(() => {
    loadSecurityData();
  }, [filters]);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load security statistics
      const statsResponse = await adminAPI.getSecurityStats();
      setSecurityStats(statsResponse.data.securityStats);

      // Load security events
      const eventsResponse = await adminAPI.getSecurityEvents(
        filters.limit,
        0,
        filters.eventType || undefined,
        filters.severity || undefined
      );
      setSecurityEvents(eventsResponse.data.events);

    } catch (err) {
      console.error('Error loading security data:', err);
      setError('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading security data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>Security Dashboard</h2>

      {error && (
        <div className="error-message" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Security Statistics */}
      {securityStats && (
        <div style={{ marginBottom: '30px' }}>
          <h3>Security Statistics</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div className="card" style={{ padding: '15px', textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#dc3545' }}>Critical Events</h4>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {securityStats.criticalEvents || 0}
              </div>
            </div>
            <div className="card" style={{ padding: '15px', textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#fd7e14' }}>High Priority</h4>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {securityStats.highEvents || 0}
              </div>
            </div>
            <div className="card" style={{ padding: '15px', textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#ffc107' }}>Medium Priority</h4>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {securityStats.mediumEvents || 0}
              </div>
            </div>
            <div className="card" style={{ padding: '15px', textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#28a745' }}>Low Priority</h4>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {securityStats.lowEvents || 0}
              </div>
            </div>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px'
          }}>
            <div className="card" style={{ padding: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Failed Login Attempts</h4>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                {securityStats.failedLogins || 0}
              </div>
              <small style={{ color: '#6c757d' }}>Last 24 hours</small>
            </div>
            <div className="card" style={{ padding: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Rate Limit Violations</h4>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fd7e14' }}>
                {securityStats.rateLimitViolations || 0}
              </div>
              <small style={{ color: '#6c757d' }}>Last 24 hours</small>
            </div>
            <div className="card" style={{ padding: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Honeypot Detections</h4>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ffc107' }}>
                {securityStats.honeypotDetections || 0}
              </div>
              <small style={{ color: '#6c757d' }}>Last 24 hours</small>
            </div>
            <div className="card" style={{ padding: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>reCAPTCHA Failures</h4>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                {securityStats.recaptchaFailures || 0}
              </div>
              <small style={{ color: '#6c757d' }}>Last 24 hours</small>
            </div>
          </div>
        </div>
      )}

      {/* Security Events */}
      <div>
        <h3>Security Events</h3>
        
        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: '15px', 
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>Event Type:</label>
            <select
              value={filters.eventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All Types</option>
              <option value="failed_login">Failed Login</option>
              <option value="rate_limit_exceeded">Rate Limit Exceeded</option>
              <option value="honeypot_detected">Honeypot Detected</option>
              <option value="recaptcha_failed">reCAPTCHA Failed</option>
              <option value="suspicious_activity">Suspicious Activity</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>Severity:</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>Limit:</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value={25}>25 events</option>
              <option value={50}>50 events</option>
              <option value={100}>100 events</option>
              <option value={200}>200 events</option>
            </select>
          </div>
          
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              onClick={loadSecurityData}
              className="btn btn-primary"
              style={{ padding: '8px 16px' }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Events Table */}
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '--bg-primary' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Timestamp
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Event Type
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Severity
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    IP Address
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {securityEvents.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                      No security events found
                    </td>
                  </tr>
                ) : (
                  securityEvents.map((event) => (
                    <tr key={event.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {formatTimestamp(event.timestamp)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          backgroundColor: '--bg-primary',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {event.eventType}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          backgroundColor: getSeverityColor(event.severity),
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {event.severity}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontFamily: 'monospace' }}>
                        {event.ipAddress}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {event.details && Object.keys(event.details).length > 0 ? (
                          <details>
                            <summary style={{ cursor: 'pointer', color: '#007bff' }}>
                              View Details
                            </summary>
                            <pre style={{ 
                              marginTop: '8px', 
                              fontSize: '12px', 
                              backgroundColor: '#f8f9fa',
                              padding: '8px',
                              borderRadius: '4px',
                              overflow: 'auto'
                            }}>
                              {JSON.stringify(event.details, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span style={{ color: '#6c757d' }}>No additional details</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;
